import type { ImgHTMLAttributes } from "react";

interface ResponsiveImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  /**
   * Whether to include retina (2x) variant
   * @default true
   */
  retina?: boolean;
}

/**
 * Responsive image with 1x/2x retina srcset and AVIF → PNG fallback via <picture>.
 *
 * Expects these files to co-exist on disk:
 * - image.png, image@2x.png
 * - image.avif, image@2x.avif
 */
export function ResponsiveImage({
  src,
  alt,
  width,
  height,
  retina = true,
  className,
  loading = "lazy",
  ...props
}: ResponsiveImageProps) {
  const lastDot = src.lastIndexOf(".");
  const basePath = lastDot === -1 ? src : src.substring(0, lastDot);
  const extension = lastDot === -1 ? "" : src.substring(lastDot);

  const pngSrcset = retina
    ? `${src} 1x, ${basePath}@2x${extension} 2x`
    : undefined;
  const avifSrc = `${basePath}.avif`;
  const avifSrcset = retina
    ? `${avifSrc} 1x, ${basePath}@2x.avif 2x`
    : avifSrc;

  return (
    <picture>
      <source type="image/avif" srcSet={avifSrcset} />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        srcSet={pngSrcset}
        className={className}
        loading={loading}
        {...props}
      />
    </picture>
  );
}
